import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import InvestorPortalLayout from '../../../components/market/InvestorPortalLayout';
import { useInvestorAuth } from '../../../context/InvestorAuthContext';
import { Icon } from '../../../utils/icons';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const PRIMARY = '#3a6ad9';

const STATUS_LABELS = {
  submitted:   'Under Review',
  loi_pending: 'LOI Pending',
  loi_signed:  'LOI Signed',
  dd_open:     'DD Room Open',
  rejected:    'Not Selected',
  withdrawn:   'Withdrawn',
};

export default function InvestorPortalDashboard() {
  const { token, investor } = useInvestorAuth();
  const [me, setMe] = useState(investor);
  const [pools, setPools] = useState([]);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const meRes = await axios.get(`${API_URL}/api/market/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!mounted) return;
        const inv = meRes.data.investor;
        setMe(inv);
        // NDA gate removed — approved investors fetch pools/bids
        // unconditionally now.
        const [p, b] = await Promise.all([
          axios.get(`${API_URL}/api/market/pools`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { pools: [] } })),
          axios.get(`${API_URL}/api/market/bids`,  { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { bids: [] } })),
        ]);
        if (mounted) {
          setPools(p.data.pools || []);
          setBids(b.data.bids || []);
        }
      } catch {}
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [token]);

  const activeBids = bids.filter((b) => !['rejected', 'withdrawn'].includes(b.status));
  const recent = bids[0];

  return (
    <InvestorPortalLayout>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#111111' }}>
          Welcome, {me?.firstName || 'Investor'}.
        </h1>
        <p style={{ margin: '6px 0 28px', fontSize: 14, color: '#6b7280' }}>
          Active mortgage pools and your bid activity.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 24 }}>
          <Card>
            <CardLabel>Active Pools</CardLabel>
            <CardValue>{loading ? '…' : pools.length}</CardValue>
            <Link to="/market/portal/pools" style={cardLink}>Browse Pools →</Link>
          </Card>
          <Card>
            <CardLabel>My Active Bids</CardLabel>
            <CardValue>{loading ? '…' : activeBids.length}</CardValue>
            {recent ? (
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                Most recent: <strong style={{ color: '#374151' }}>{STATUS_LABELS[recent.status] || recent.status}</strong>
              </div>
            ) : null}
            <Link to="/market/portal/bids" style={cardLink}>View Bids →</Link>
          </Card>
          <Card>
            <CardLabel>Account Status</CardLabel>
            <CardValue style={{ textTransform: 'capitalize' }}>{me?.status || '—'}</CardValue>
          </Card>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginBottom: 24 }}>
          <QuickLink to="/market/portal/messages"  iconName="chat"   label="Messages" />
          <QuickLink to="/market/portal/alerts"    iconName="bell"   label="Alerts" />
          <QuickLink to="/market/portal/resources" iconName="book"   label="Resources" />
          <QuickLink to="/market/portal/documents" iconName="folder" label="My Documents" />
        </div>

        {!loading && pools.length === 0 && (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 28, textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111111', marginBottom: 6 }}>Pool listings coming soon</div>
            <div style={{ fontSize: 14, color: '#6b7280' }}>
              Doorly's investor pools will be published soon. You'll be emailed when new pools go live.
            </div>
          </div>
        )}
      </div>
    </InvestorPortalLayout>
  );
}

const Card = ({ children }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 20 }}>{children}</div>
);
const CardLabel = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>{children}</div>
);
const CardValue = ({ children, style }) => (
  <div style={{ fontSize: 26, fontWeight: 800, color: '#111111', marginBottom: 8, ...style }}>{children}</div>
);
const cardLink = { fontSize: 13, fontWeight: 700, color: PRIMARY, textDecoration: 'none' };

const QuickLink = ({ to, iconName, label }) => (
  <Link to={to}
    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, textDecoration: 'none', color: '#111111', fontWeight: 600, fontSize: 13.5 }}>
    <span style={{ width: 32, height: 32, borderRadius: 8, background: '#eff3fe', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon name={iconName} size={16} color={PRIMARY} />
    </span>
    {label}
    <span style={{ marginLeft: 'auto', color: '#9ca3af' }}>→</span>
  </Link>
);
